<script setup lang="ts">
    import { onMounted, reactive, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import axios from 'axios';
    import OnboardingPersonalInfosForm from './OnboardingPersonalInfosForm.vue';
    import OnboardingIdentityDocumentsForm from './OnboardingIdentityDocumentsForm.vue';
    import OnboardingPayslipDocumentsForm from './OnboardingPayslipDocumentsForm.vue';

    const router = useRouter();
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const currentStepIndex = ref(1);
    const formParams = reactive({
        collectivites: [] as any[],
        types_piece_identites: [] as any[],
    });
    const formDatas = reactive({
        user: null as any,
        photo: null as any,
        type_piece_identite: null as any,
        numero_piece_identite: null as any,
        piece_recto: null as any,
        piece_verso: null as any,
        bulletin1: null as any,
        bulletin2: null as any,
        bulletin3: null as any,
    });
    onMounted(() => {
        const form_datas = route.meta.form_datas as { collectivites: any[], user: any, types_piece_identites: any[] };
        Object.assign(formParams, form_datas);
    });

    function goToStep(stepIndex: number) {
        currentStepIndex.value = stepIndex;
    }

    function onAccountCreated(user: any) {
        formDatas.user = user;
        goToStep(2);
    }

    function onIdentityDocumentsSaved(identityDocs: any) {
        formDatas.photo = identityDocs.photo;
        formDatas.type_piece_identite = identityDocs.type_piece_identite;
        formDatas.numero_piece_identite = identityDocs.numero_piece_identite;
        formDatas.piece_recto = identityDocs.piece_recto;
        formDatas.piece_verso = identityDocs.piece_verso;
        goToStep(3);
    }

    function onPayslipDocumentsSaved(payslipDocs: any) {
        formDatas.bulletin1 = payslipDocs.bulletin1;
        formDatas.bulletin2 = payslipDocs.bulletin2;
        formDatas.bulletin3 = payslipDocs.bulletin3;
        doSaveKYCDocuments();
    }

    function validate() {
        let isValid = true;
        if (!formDatas.user) {
            isValid = false;
        }
        if (!formDatas.photo) {
            isValid = false;
        }
        if (!formDatas.piece_recto) {
            isValid = false;
        }
        if (!formDatas.piece_verso) {
            isValid = false;
        }
        if (!formDatas.bulletin1) {
            isValid = false;
        }
        if (!formDatas.bulletin2) {
            isValid = false;
        }
        if (!formDatas.bulletin3) {
            isValid = false;
        }
        return isValid;
    }

    async function doSaveKYCDocuments() {
        if (!validate()) { return; }

        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = new FormData();
            formToUpload.append("nom", formDatas.user.nom);
            formToUpload.append("prenom", formDatas.user.prenom);
            formToUpload.append("date_naissance", formDatas.user.date_naissance);
            formToUpload.append("age", formDatas.user.age.toString());
            formToUpload.append("date_engagement", formDatas.user.date_engagement);
            formToUpload.append("anciennete", formDatas.user.anciennete.toString());
            formToUpload.append("collectivite_id", formDatas.user.collectivite_id.toString());
            formToUpload.append("salaire", formDatas.user.salaire);
            formToUpload.append("quotite_cessible_pourcent", formDatas.user.quotite_cessible_pourcent);
            formToUpload.append("quotite_cessible_montant", formDatas.user.quotite_cessible_montant);
            formToUpload.append("telephone", formDatas.user.telephone);
            formToUpload.append("email", formDatas.user.email);

            formToUpload.append("photo", formDatas.photo.file);
            formToUpload.append("type_piece_identite", formDatas.type_piece_identite);
            formToUpload.append("numero_piece_identite", formDatas.numero_piece_identite);
            formToUpload.append("piece_recto", formDatas.piece_recto.file);
            formToUpload.append("piece_verso", formDatas.piece_verso.file);
            formToUpload.append("bulletin1", formDatas.bulletin1.file);
            formToUpload.append("bulletin2", formDatas.bulletin2.file);
            formToUpload.append("bulletin3", formDatas.bulletin3.file);
            const response = await axios.post(`/backoffice/onboarding/create_account`, formToUpload);
            router.push(`/app/users/${response.data.user_id}`);
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }

</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-30"></div>

        <div class="widget px-8 py-7 s20">
            <div v-show="currentStepIndex == 1">
                <onboarding-personal-infos-form 
                    :collectivites="formParams.collectivites"
                    @saved="onAccountCreated"></onboarding-personal-infos-form>
            </div>
            <div v-show="currentStepIndex == 2">
                <div v-if="formDatas.user">
                    <onboarding-identity-documents-form 
                        :user="formDatas.user" 
                        :types_piece_identites="formParams.types_piece_identites" 
                        @saved="onIdentityDocumentsSaved">
                    </onboarding-identity-documents-form>
                </div>
            </div>
            <div v-show="currentStepIndex == 3">
                <div v-if="formDatas.user">
                    <onboarding-payslip-documents-form 
                        :form-params="{ user: formDatas.user, photo: formDatas.photo }"
                        :loading="loading"
                        @saved="onPayslipDocumentsSaved"
                        @previous="goToStep(2)">
                    </onboarding-payslip-documents-form>
                </div>
            </div>
            <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
        </div>


        <div class="clear-both h-30"></div>

    </section>
</template>
